import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { 
  ArrowLeftIcon, 
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  TruckIcon,
  PrinterIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

type OrderItem = {
  id: string;
  name: string;
  quantity: number;
  price: number;
};

type Order = {
  id: string;
  customer: {
    id: string;
    name: string;
    email: string;
    phone?: string;
    address?: {
      street: string;
      city: string;
      state: string;
      zipCode: string;
    };
  };
  date: string;
  total: number;
  status: 'pending' | 'processing' | 'completed' | 'cancelled';
  items: OrderItem[];
  notes?: string;
  pickupTime?: string;
};

export default function OrderDetails() {
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    if (!orderId) return;
    
    setIsLoading(true);
    try {
      // In a real app, this would be an API call
      // const data = await getOrderById(orderId);
      
      // Mock data for demonstration
      const data: Order = {
        id: orderId,
        customer: {
          id: 'c101',
          name: 'Local Food Bank',
          email: 'contact@localfoodbank.org',
          phone: '(555) 123-4567',
          address: {
            street: '123 Main St',
            city: 'Anytown',
            state: 'CA',
            zipCode: '12345'
          }
        },
        date: '2025-05-05T14:30:00Z',
        total: 125.50,
        status: 'processing',
        items: [
          { id: 'i101', name: 'Organic Apples (5lb bag)', quantity: 5, price: 4.99 },
          { id: 'i102', name: 'Whole Grain Bread', quantity: 10, price: 2.99 },
          { id: 'i103', name: 'Fresh Milk (1 gallon)', quantity: 8, price: 3.49 }
        ],
        notes: 'Please package items separately. Will pick up at the loading dock.',
        pickupTime: '2025-05-06T15:00:00Z'
      };
      
      setOrder(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch order details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: Order['status']) => {
    if (!order) return;
    
    setIsUpdating(true);
    try {
      // In a real app, this would be an API call
      // await updateOrderStatus(order.id, newStatus);
      
      // Update local state
      setOrder({
        ...order,
        status: newStatus
      });
    } catch (err: any) {
      setError(err.message || 'Failed to update order status');
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusIcon = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'processing':
        return <TruckIcon className="h-5 w-5 text-blue-500" />;
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'cancelled':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: Order['status']) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusClass = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return '';
    }
  };

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Layout title="Order Details">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-blue"></div>
        </div>
      </Layout>
    );
  }

  if (error || !order) {
    return (
      <Layout title="Order Details">
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error || 'Order not found'}</p>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button
            type="button"
            onClick={() => navigate('/orders')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-navy-blue hover:bg-sky-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue"
          >
            Back to Orders
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={`Order #${order.id}`}>
      <div className="space-y-6">
        <div className="flex items-center">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="mr-4 text-gray-500 hover:text-navy-blue"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <h1 className="text-xl font-medium text-navy-blue">  #{order.id}</h1>
          <div className="ml-4 flex items-center">
            {getStatusIcon(order.status)}
            <span className={`ml-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusClass(order.status)}`}>
              {getStatusText(order.status)}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Order Summary */}
          <div className="md:col-span-2 space-y-6">
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-medium text-navy-blue">Order Summary</h2>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue"
                  >
                    <PrinterIcon className="h-4 w-4 mr-1" />
                    Print
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue"
                  >
                    <EnvelopeIcon className="h-4 w-4 mr-1" />
                    Email
                  </button>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Order Date</p>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(order.date)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Pickup Time</p>
                    <p className="mt-1 text-sm text-gray-900">
                      {order.pickupTime ? formatDate(order.pickupTime) : 'Not specified'}
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Order Items</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Item
                          </th>
                          <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Qty
                          </th>
                          <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Price
                          </th>
                          <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {order.items.map((item) => (
                          <tr key={item.id}>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {item.name}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 text-right">
                              {item.quantity}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 text-right">
                              {formatCurrency(item.price)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                              {formatCurrency(item.price * item.quantity)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <th scope="row" colSpan={3} className="px-3 py-2 text-right text-sm font-medium text-gray-900">
                            Total
                          </th>
                          <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                            {formatCurrency(order.total)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {order.notes && (
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Notes</h3>
                    <p className="text-sm text-gray-500">{order.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Customer Info and Actions */}
          <div className="space-y-6">
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-navy-blue">Customer Information</h2>
              </div>
              <div className="p-6">
                <h3 className="text-sm font-medium text-gray-900">{order.customer.name}</h3>
                <p className="mt-1 text-sm text-gray-500">{order.customer.email}</p>
                {order.customer.phone && (
                  <p className="mt-1 text-sm text-gray-500">{order.customer.phone}</p>
                )}
                
                {order.customer.address && (
                  <div className="mt-4">
                    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Address</h4>
                    <p className="mt-1 text-sm text-gray-900">{order.customer.address.street}</p>
                    <p className="text-sm text-gray-900">
                      {order.customer.address.city}, {order.customer.address.state} {order.customer.address.zipCode}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-navy-blue">Order Actions</h2>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {order.status === 'pending' && (
                    <>
                      <button
                        type="button"
                        onClick={() => handleStatusUpdate('processing')}
                        disabled={isUpdating}
                        className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-navy-blue hover:bg-sky-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue disabled:opacity-50"
                      >
                        {isUpdating ? 'Updating...' : 'Start Processing'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStatusUpdate('cancelled')}
                        disabled={isUpdating}
                        className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                      >
                        {isUpdating ? 'Updating...' : 'Cancel Order'}
                      </button>
                    </>
                  )}
                  
                  {order.status === 'processing' && (
                    <button
                      type="button"
                      onClick={() => handleStatusUpdate('completed')}
                      disabled={isUpdating}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                    >
                      {isUpdating ? 'Updating...' : 'Mark as Completed'}
                    </button>
                  )}
                  
                  {order.status === 'completed' && (
                    <div className="bg-green-50 rounded-md p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <CheckCircleIcon className="h-5 w-5 text-green-400" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-green-800">
                            This order has been completed
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {order.status === 'cancelled' && (
                    <div className="bg-red-50 rounded-md p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <XCircleIcon className="h-5 w-5 text-red-400" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-red-800">
                            This order has been cancelled
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
